import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  extractDriveFolderId,
  driveThumbUrl,
  driveViewUrl,
} from "../utils/drive";

const LS_FOLDER = "galleryDriveFolderId";
const LS_ITEMS = "galleryItems";
const LS_API = "galleryApiUrl";

/* ---------- THUMBNAIL (robust, no-referrer + fallbacks) ---------- */
function ThumbImg({
  id,
  size = 800,
  className = "",
  altText = "Montaj parbriz RedGlassAuto",
}) {
  const [src, setSrc] = useState(driveThumbUrl(id, size));
  const [attempt, setAttempt] = useState(0); // 0: thumb -> 1: view -> 2: tiny thumb

  const handleError = () => {
    if (attempt === 0) {
      setSrc(driveViewUrl(id)); // try direct view
      setAttempt(1);
      return;
    }
    if (attempt === 1) {
      setSrc(driveThumbUrl(id, 200)); // tiny thumb as last resort
      setAttempt(2);
      return;
    }
  };

  return (
    <img
      src={src}
      alt={altText}
      className={className}
      loading="lazy"
      decoding="async"
      draggable="false"
      referrerPolicy="no-referrer"
      onError={handleError}
    />
  );
}

/* ---------- LIGHTBOX (fullscreen carousel) ---------- */
function Lightbox({ items, index, onClose, onPrev, onNext, alts = [] }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [src, setSrc] = useState("");
  const [fallbackTried, setFallbackTried] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // Load image when index changes
  useEffect(() => {
    setErr("");
    setFallbackTried(false);
    if (index >= 0 && index < items.length) {
      const id = items[index];
      setLoading(true);
      setSrc(driveViewUrl(id)); // primary
    }
  }, [index, items]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  // Lock scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Touch swipe
  useEffect(() => {
    let startX = 0,
      startY = 0;
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx > 0 ? onPrev() : onNext();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onPrev, onNext]);

  if (index < 0 || index >= items.length) return null;
  const id = items[index];
  const alt = alts[index] || "Montaj parbriz RedGlassAuto – imagine mare";

  const handleError = () => {
    if (!fallbackTried) {
      setFallbackTried(true);
      setSrc(driveThumbUrl(id, 2000)); // large thumbnail fallback
      return;
    }
    setErr(
      "Imaginea nu poate fi încărcată. Verifică permisiunile din Google Drive."
    );
    setLoading(false);
  };
  const handleLoad = () => setLoading(false);

  return (
    <div ref={containerRef} className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />

      {/* Close */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          className="rounded-full bg-white/20 hover:bg-white/30 text-white px-4 py-2"
        >
          ✕
        </button>
      </div>

      {/* GLOBAL spinner overlay — fixed to viewport, never moves */}
      {loading && (
        <div className="pointer-events-none fixed inset-0 z-[60] grid place-items-center">
          <div className="rounded-full bg-black/40 p-3">
            <svg
              className="h-6 w-6 animate-spin text-white"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-20"
              />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-80"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="absolute inset-0 grid place-items-center p-4">
        <figure className="relative max-h-[90vh] max-w-[95vw]">
          {src && !err ? (
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              className={
                "max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-lg " +
                "transition-opacity duration-200 " +
                (loading ? "opacity-0" : "opacity-100")
              }
              loading="eager"
              decoding="async"
              referrerPolicy="no-referrer"
              onLoad={handleLoad}
              onError={handleError}
            />
          ) : (
            <div className="text-white/90 text-sm bg-black/40 px-3 py-2 rounded">
              {err || "Se încarcă..."}
            </div>
          )}

          {/* Hit zones; disabled/hidden while loading */}
          <button
            onClick={onPrev}
            aria-label="Previous"
            disabled={loading}
            className={
              "absolute inset-y-0 left-0 w-1/3 flex items-center justify-start px-2 focus:outline-none " +
              (loading ? "pointer-events-none" : "")
            }
            style={{ background: "transparent" }}
          >
            {!loading && (
              <span className="text-white/70 text-4xl leading-none select-none">
                ‹
              </span>
            )}
          </button>
          <button
            onClick={onNext}
            aria-label="Next"
            disabled={loading}
            className={
              "absolute inset-y-0 right-0 w-1/3 flex items-center justify-end px-2 focus:outline-none " +
              (loading ? "pointer-events-none" : "")
            }
            style={{ background: "transparent" }}
          >
            {!loading && (
              <span className="text-white/70 text-4xl leading-none select-none">
                ›
              </span>
            )}
          </button>
        </figure>
      </div>
    </div>
  );
}

/* ---------- PAGE ---------- */
export default function Gallery({ setPage }) {
  const { t } = useTranslation("common");

  // Read saved config
  const rawFolder =
    (typeof window !== "undefined" && localStorage.getItem(LS_FOLDER)) || "";
  const folderId = useMemo(() => extractDriveFolderId(rawFolder), [rawFolder]);

  // Prefer API (Google Apps Script URL), else manual
  const apiUrl =
    (typeof window !== "undefined" && localStorage.getItem(LS_API)) || "";

  // Manual fallback list
  const rawItems =
    (typeof window !== "undefined" && localStorage.getItem(LS_ITEMS)) || "";
  const manualIds = useMemo(
    () =>
      rawItems
        ? rawItems
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    [rawItems]
  );

  const [fileIds, setFileIds] = useState([]);
  const [loading, setLoading] = useState(!!apiUrl);
  const [error, setError] = useState("");

  const alts = useMemo(
    () => fileIds.map((_, i) => `Montaj parbriz RedGlassAuto – foto ${i + 1}`),
    [fileIds]
  );

  useEffect(() => {
    let aborted = false;
    async function load() {
      if (!apiUrl) {
        setFileIds(manualIds);
        setLoading(false);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const url = folderId
          ? `${apiUrl}?folder=${encodeURIComponent(folderId)}`
          : apiUrl;
        const res = await fetch(url, { mode: "cors", cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const ids = (data.items || []).map((x) => x.id).filter(Boolean);
        if (!aborted) {
          setFileIds(ids);
          setLoading(false);
        }
      } catch (e) {
        if (!aborted) {
          setError("Nu s-a putut încărca lista din API.");
          setFileIds(manualIds);
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, [apiUrl, folderId, manualIds]);

  const [lbIndex, setLbIndex] = useState(-1);

  const openAt = useCallback((i) => {
    setLbIndex(i);
    window.history.pushState({ lightbox: true, i }, "", `#photo-${i + 1}`);
  }, []);

  const prev = useCallback(() => {
    setLbIndex((i) => {
      const ni = i > 0 ? i - 1 : Math.max(0, fileIds.length - 1);
      window.history.replaceState(
        { lightbox: true, i: ni },
        "",
        `#photo-${ni + 1}`
      );
      return ni;
    });
  }, [fileIds.length]);

  const next = useCallback(() => {
    setLbIndex((i) => {
      const ni = (i + 1) % Math.max(1, fileIds.length);
      window.history.replaceState(
        { lightbox: true, i: ni },
        "",
        `#photo-${ni + 1}`
      );
      return ni;
    });
  }, [fileIds.length]);

  const closeLb = useCallback(() => {
    setLbIndex(-1);
    if (window.location.hash.startsWith("#photo-")) {
      const clean = window.location.href.replace(/#photo-\d+$/, "");
      window.history.replaceState({}, "", clean);
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      if (window.location.hash.startsWith("#photo-")) {
        const n = parseInt(window.location.hash.replace("#photo-", ""), 10);
        if (!isNaN(n) && n >= 1 && n <= fileIds.length) {
          setLbIndex(n - 1);
          return;
        }
      }
      setLbIndex(-1);
    };
    window.addEventListener("popstate", onPop);
    onPop();
    return () => window.removeEventListener("popstate", onPop);
  }, [fileIds.length]);

  const hasCustomGrid = fileIds.length > 0;

  return (
    <main>
      <section id="gallery" className="max-w-6xl mx-auto px-4 pt-10 pb-12">
        {/* Back */}
        <div className="mb-5">
          <button
            onClick={() => {
              setPage("home");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="
      group inline-flex items-center gap-2
      rounded-full px-5 py-2.5
      bg-redglass-primary text-white
      shadow-brand ring-1 ring-redglass-ring/40
      hover:bg-redglass-dark active:translate-y-px
      transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redglass-primary/60
    "
            aria-label={t("gallery.backHome")}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-5 w-5 -ml-1 transition-transform group-hover:-translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span className="font-medium">{t("gallery.backHome")}</span>
          </button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-2 text-redglass-ink">
          {t("gallery.title")}
        </h1>

        {/* Loading message directly under the title */}
        {loading && (
          <div className="mb-6 flex items-center gap-2 text-sm text-redglass-ink bg-white/60 border border-white/70 backdrop-blur rounded-xl px-3 py-2">
            <svg
              className="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-20"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-80"
              />
            </svg>
            <span>{t("gallery.empty")}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-2xl bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        ) : hasCustomGrid ? (
          <>
            <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {fileIds.map((id, i) => (
                <div
                  key={id + i}
                  className="aspect-square rounded-2xl overflow-hidden border bg-white"
                >
                  <button
                    onClick={() => openAt(i)}
                    className="w-full h-full"
                    title="Deschide imaginea"
                  >
                    <ThumbImg
                      id={id}
                      size={800}
                      altText={alts[i]}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.03]"
                    />
                  </button>
                </div>
              ))}
            </div>

            {lbIndex >= 0 && (
              <Lightbox
                items={fileIds}
                index={lbIndex}
                onClose={closeLb}
                onPrev={prev}
                onNext={next}
                alts={alts}
              />
            )}
          </>
        ) : null}
      </section>
    </main>
  );
}
